/** Debounced, idempotent persistence for the Excalidraw drawing layer — the
 * (much simpler) counterpart to the InkModel's batching queue, which serves
 * the classic Fabric engine. Neither replaced the other: classic is the
 * default and the shipped product (stores/writing-engine.ts), and Excalidraw
 * is the opt-in alternative.
 *
 * Excalidraw's onChange fires on pointer moves and selection churn, so the
 * controller only saves when the SCENE SIGNATURE moves: the sorted
 * `id:version` set of live elements (Excalidraw bumps `version` on every real
 * mutation) plus the persisted
 * appState subset. One whole-scene PUT replaces the old per-op batches; a
 * replayed PUT is naturally idempotent.
 *
 * Failure posture (inherited from the InkModel): transient failures retry with
 * backoff, then PARK — the scene stays authoritative in the editor and the
 * controller replays when connectivity returns (`online` + a slow poll). A
 * definitive 4xx is surfaced and dropped (retrying can't fix the client's
 * fault). On pagehide the pending scene drains through `fetch(keepalive)` so a
 * tab close doesn't lose the last strokes. */

import { GraphMindApiError, type DrawingScene } from "@/lib/api";

import { putDrawing } from "./scene-io";

const FLUSH_DELAY_MS = 500;
const REPLAY_DELAY_MS = 5_000;

/** A failure worth retrying: network/abort (no status), cold-auth 401, or a
 * transient server condition. Other 4xx are the client's fault. */
export function isTransient(e: unknown): boolean {
  if (e instanceof GraphMindApiError) {
    return e.status === 401 || e.status === 408 || e.status === 429 || e.status >= 500;
  }
  return true;
}

async function withRetry<T>(op: () => Promise<T>, attempts = 3): Promise<T> {
  for (let i = 0; ; i++) {
    try {
      return await op();
    } catch (e) {
      if (i >= attempts - 1 || !isTransient(e)) throw e;
      await new Promise((r) => setTimeout(r, 200 * 2 ** i)); // 200ms, 400ms
    }
  }
}

/** The change-detection key: cheap to compute on every onChange tick. */
export function sceneSignature(scene: DrawingScene): string {
  const ids = scene.elements
    .map((e) => `${String(e.id)}:${Number(e.version) || 0}`)
    .sort()
    .join(",");
  return `${ids}|${JSON.stringify(scene.appState)}`;
}

export class SaveController {
  private readonly docId: string;
  private readonly onTrouble?: () => void;
  private readonly onReject?: () => void;
  private readonly onSaved?: (scene: DrawingScene) => void;

  private signature: string | null = null;
  private pending: DrawingScene | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | undefined;
  private replayTimer: ReturnType<typeof setTimeout> | undefined;
  private inflight = false;
  private parked = false;
  private disposed = false;

  private readonly onOnline = () => this.replayNow();
  private readonly onPageHide = () => this.drain();

  constructor(opts: {
    docId: string;
    /** The signature of the scene as loaded, so the first onChange tick (which
     * fires immediately with the initial data) never issues a no-op PUT. */
    initialSignature: string | null;
    onTrouble?: () => void;
    onReject?: () => void;
    /** A PUT landed — lets the owner keep the react-query cache honest. */
    onSaved?: (scene: DrawingScene) => void;
  }) {
    this.docId = opts.docId;
    this.signature = opts.initialSignature;
    this.onTrouble = opts.onTrouble;
    this.onReject = opts.onReject;
    this.onSaved = opts.onSaved;
    window.addEventListener("online", this.onOnline);
    window.addEventListener("pagehide", this.onPageHide);
  }

  /** Called from Excalidraw's onChange with the current wire scene. */
  update(scene: DrawingScene): void {
    if (this.disposed) return;
    const sig = sceneSignature(scene);
    if (sig === this.signature && this.pending === null) return;
    if (this.pending !== null && sig === sceneSignature(this.pending)) return;
    this.pending = scene;
    clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => void this.flush(), FLUSH_DELAY_MS);
  }

  get dirty(): boolean {
    return this.pending !== null;
  }

  /** Flush anything pending right now (awaitable). Currently UNUSED: it has no
   * caller. Unmount goes through `dispose()`, which rides the pending scene out
   * on the keepalive PUT in `drain()` instead of awaiting a normal flush. */
  async flushNow(): Promise<void> {
    clearTimeout(this.flushTimer);
    await this.flush();
  }

  dispose(): void {
    this.drain(); // a dirty scene rides the keepalive PUT out
    this.disposed = true;
    clearTimeout(this.flushTimer);
    clearTimeout(this.replayTimer);
    window.removeEventListener("online", this.onOnline);
    window.removeEventListener("pagehide", this.onPageHide);
  }

  private async flush(): Promise<void> {
    if (this.inflight || this.pending === null || this.disposed) return;
    const scene = this.pending;
    this.inflight = true;
    try {
      await withRetry(() => putDrawing(this.docId, scene));
      this.signature = sceneSignature(scene);
      if (this.pending === scene) this.pending = null;
      this.parked = false;
      this.onSaved?.(scene);
    } catch (e) {
      if (isTransient(e)) {
        // Park: keep `pending` and replay on connectivity / a slow poll.
        if (!this.parked) {
          this.parked = true;
          this.onTrouble?.();
        }
        clearTimeout(this.replayTimer);
        this.replayTimer = setTimeout(() => this.replayNow(), REPLAY_DELAY_MS);
      } else {
        // Definitive rejection — drop it so one bad scene can't wedge saving
        // forever (the next edit produces a fresh scene to try).
        if (this.pending === scene) this.pending = null;
        this.onReject?.();
      }
    } finally {
      this.inflight = false;
      // Edits that arrived mid-PUT re-dirtied `pending` — follow up.
      if (this.pending !== null && !this.parked) {
        clearTimeout(this.flushTimer);
        this.flushTimer = setTimeout(() => void this.flush(), FLUSH_DELAY_MS);
      }
    }
  }

  private replayNow(): void {
    if (this.disposed || this.pending === null) return;
    void this.flush();
  }

  /** Unload-safe drain: `keepalive` lets the browser finish the PUT after the
   * page is gone (same-origin cookies carry auth — the flushNodeText pattern).
   * Bodies over the keepalive cap (~64 KiB) are skipped — the debounced save
   * was at most 500ms behind, so the exposure is one pen stroke. */
  private drain(): void {
    if (this.pending === null) return;
    const body = JSON.stringify(this.pending);
    if (body.length > 60_000) return;
    // B RULE-006 exception, removed by remediation item 4.1d. The pagehide
    // drain needs `keepalive`, which `api.request` does not expose yet; 4.1d
    // adds it to the client and routes this call through it. Do not copy this
    // pattern — it is the one place where losing the request loses user ink.
    // eslint-disable-next-line no-restricted-globals
    void fetch(`/api/documents/${this.docId}/drawing`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      /* unload path — nothing we can surface */
    });
    this.pending = null;
  }
}
